import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { clerkErrorMessage } from "../utils/clerkError";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_MS = 60_000;

export const INVALID_CODE_MESSAGE = "That code isn't valid. Please try again.";

function formatCountdown(remainingMs: number) {
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

type OtpVerificationProps = {
  backLabel: string;
  backTo: string;
  isLoaded: boolean;
  onVerify: (code: string) => Promise<string | void>;
  onResend: () => Promise<void>;
};

export default function OtpVerification({
  backLabel,
  backTo,
  isLoaded,
  onVerify,
  onResend,
}: OtpVerificationProps) {
  const navigate = useNavigate();

  const [code, setCode] = useState<string[]>(() =>
    Array<string>(OTP_LENGTH).fill(""),
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [cooldownEndsAt, setCooldownEndsAt] = useState(
    () => Date.now() + RESEND_COOLDOWN_MS,
  );
  const [cooldownMs, setCooldownMs] = useState(RESEND_COOLDOWN_MS);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const tick = () => setCooldownMs(Math.max(cooldownEndsAt - Date.now(), 0));

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => window.clearInterval(intervalId);
  }, [cooldownEndsAt]);

  const hasError = errorMessage !== "";
  const canResend = cooldownMs === 0;
  const isCodeComplete = code.every((digit) => digit !== "");

  const focusInput = (index: number) => {
    if (index < 0 || index >= OTP_LENGTH) return;
    inputsRef.current[index]?.focus();
  };

  const fillFrom = (index: number, digits: string) => {
    setErrorMessage("");
    setCode((prev) => {
      const next = [...prev];
      for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i += 1) {
        next[index + i] = digits[i];
      }
      return next;
    });
    focusInput(Math.min(index + digits.length, OTP_LENGTH - 1));
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    setErrorMessage("");
    setCode((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit) focusInput(index + 1);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Backspace" || code[index]) return;

    event.preventDefault();
    setErrorMessage("");
    setCode((prev) => {
      const next = [...prev];
      next[index - 1] = "";
      return next;
    });
    focusInput(index - 1);
  };

  const handlePaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!digits) return;

    event.preventDefault();
    fillFrom(index, digits);
  };

  const handleResend = async () => {
    if (!isLoaded || isResending || !canResend) return;

    setIsResending(true);
    setErrorMessage("");

    try {
      await onResend();

      setCode(Array<string>(OTP_LENGTH).fill(""));
      focusInput(0);
      setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_MS);
    } catch (error) {
      setErrorMessage(clerkErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async () => {
    if (!isLoaded || isVerifying || !isCodeComplete) return;

    setIsVerifying(true);
    setErrorMessage("");

    try {
      const failureMessage = await onVerify(code.join(""));
      if (failureMessage) setErrorMessage(failureMessage);
    } catch (error) {
      setErrorMessage(clerkErrorMessage(error, INVALID_CODE_MESSAGE));
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-sans">
      <div className="w-full max-w-xl md:ml-28 mt-9 px-4 md:px-0">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          {backLabel}
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-xl shadow-sm border border-slate-100 md:px-10 md:py-9 px-6 py-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center size-14 rounded-full bg-blue-50">
                <ShieldCheckIcon className="size-6 text-blue-600" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="md:text-2xl md:font-semibold text-xl font-medium text-gray-950">
                  OTP Verification
                </h1>
                <p className="text-sm md:font-medium font-normal text-gray-400">
                  Enter the 6-digit code sent to your email.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      inputsRef.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    autoFocus={index === 0}
                    maxLength={1}
                    value={digit}
                    aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                    aria-invalid={hasError}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) =>
                      handleChange(index, event.target.value)
                    }
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={(event) => handlePaste(index, event)}
                    className={clsx(
                      "w-11 h-14 sm:w-14 sm:h-16 rounded-xl border text-center text-lg font-medium outline-none focus:ring-2",
                      hasError
                        ? "border-red-500 text-red-500 focus:ring-red-500"
                        : "border-gray-200 text-gray-950 focus:ring-blue-500",
                    )}
                  />
                ))}
              </div>

              {errorMessage && (
                <p className="text-sm text-center text-red-500">
                  {errorMessage}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={handleVerify}
                disabled={!isLoaded || isVerifying || !isCodeComplete}
                className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isVerifying ? "Verifying…" : "Verify code"}
              </button>

              <p className="text-sm text-center text-gray-400">
                Didn't receive the code?{" "}
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={!isLoaded || isResending}
                    className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isResending ? "Sending…" : "Resend"}
                  </button>
                ) : (
                  <span className="font-medium text-blue-600">
                    Resend in {formatCountdown(cooldownMs)}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
